// src/features/crypto-ranking/CryptoRankingPage.tsx
//
// Crypto ranking feature page. Sibling of
// features/commodities-ranking/CommoditiesRankingPage. Loads the ranked
// cross-section from GET /api/v1/crypto-ranking/universe (a single flat
// payload — crypto has no category partition, so there are no filter
// pills and no ?category= refetch). A single batched call to
// GET /api/v1/crypto-ranking/historical-batch powers the table sparklines
// for the currently-visible rows (no N+1 waterfall). Reuses the standard
// <Layout sidebar={<Sidebar />}> dashboard shell so it sits behind the
// same auth gate / navigation as the rest of the suite.
//
// Crypto has no per-symbol detail endpoint (no COT / quick-stats), so —
// unlike commodities — there is no click-to-open pop-over; instead the
// second chart is a top-coin factor radar (the structural analogue of the
// commodities category radar).

import { useEffect, useMemo, useState } from 'react';

import { GlobalSearchBar } from '../global-search';
import { Layout, Sidebar } from '../../shared/ui';
import {
    fetchHistoricalBatch,
    fetchUniverse,
    type HistoricalBatchResponse,
    type UniverseRow,
} from './api';
import { CryptoDetailPopup } from './components/CryptoDetailPopup';
import { FactorRadar } from './components/FactorRadar';
import { RiskReturnScatter } from './components/RiskReturnScatter';
import { UniverseTable, type SortKey } from './components/UniverseTable';

// Cap the batched sparkline fetch to the top N rows of the current sort.
// The liquidity-screened crypto universe is small (~18-50 names) so this
// rarely bites, but it keeps the contract identical to the sibling pages.
const SPARKLINE_LIMIT = 60;

export const CryptoRankingPage = () => {
    const [universe, setUniverse] = useState<UniverseRow[]>([]);
    const [asOfDate, setAsOfDate] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [sortKey, setSortKey] = useState<SortKey>('universe_rank');
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [hoverSymbol, setHoverSymbol] = useState<string | null>(null);
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

    const [sparklines, setSparklines] = useState<HistoricalBatchResponse>({});

    // --- Universe: loaded ONCE on mount (single flat cross-section). ----
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchUniverse();
                if (cancelled) return;
                setUniverse(res.items);
                setAsOfDate(res.as_of_date);
                setError(null);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load crypto ranking universe', err);
                setError(
                    'Unable to load crypto rankings right now. Please try again shortly.',
                );
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // --- Client-side SORT. ----------------------------------------------
    const processed = useMemo(() => {
        const sorted = [...universe].sort((a, b) => {
            const va = a[sortKey];
            const vb = b[sortKey];
            let cmp: number;
            if (typeof va === 'string' && typeof vb === 'string') {
                cmp = va.localeCompare(vb);
            } else {
                cmp = (va as number) - (vb as number);
            }
            return sortDesc ? -cmp : cmp;
        });
        return sorted;
    }, [universe, sortKey, sortDesc]);

    // --- Symbols whose sparklines we batch-fetch (top of current view). -
    const sparklineSymbols = useMemo(
        () => processed.slice(0, SPARKLINE_LIMIT).map((r) => r.symbol),
        [processed],
    );
    const sparklineSymbolSet = useMemo(
        () => new Set(sparklineSymbols),
        [sparklineSymbols],
    );

    // --- Batched sparkline fetch, refreshed when the visible set changes.
    useEffect(() => {
        if (sparklineSymbols.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchHistoricalBatch(sparklineSymbols, 90);
                if (cancelled) return;
                // Merge so previously-loaded symbols persist when the
                // visible window shifts (avoids a flash of empty trends).
                setSparklines((prev) => ({ ...prev, ...res }));
            } catch (err) {
                // Sparklines are non-critical; never surface as a page error.
                // eslint-disable-next-line no-console
                console.warn('Failed to load sparkline history', err);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Join key keeps the effect stable when the array identity changes
        // but the contents don't.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sparklineSymbols.join(',')]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDesc((d) => !d);
        } else {
            setSortKey(key);
            // Rank/symbol read best ascending; factor columns best
            // descending (strongest first).
            setSortDesc(key !== 'universe_rank' && key !== 'symbol');
        }
    };

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Crypto Rankings"
            subtitle="Cross-sectional momentum, volatility & liquidity scores for the liquid digital-asset universe."
        >
            {/* Platform-wide asset search — jump to any deep-dive page. */}
            <div className="mb-8">
                <GlobalSearchBar />
            </div>

            {/* Header row: freshness badge. */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <p className="muted text-sm">
                    {universe.length > 0
                        ? `${universe.length} liquidity-screened coins`
                        : ''}
                </p>
                {asOfDate && (
                    <span className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800/50 px-2 py-1 rounded">
                        Updated: {asOfDate}
                    </span>
                )}
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400">
                    Loading quantitative data…
                </div>
            ) : (
                <>
                    {/* Charts: cross-sectional Risk/Return + top-coin radar. */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Risk vs Return
                            </h2>
                            <p className="muted text-xs mb-4">
                                {processed.length} coins · annualized vol vs
                                Forge Ultra Signal
                            </p>
                            <RiskReturnScatter
                                data={processed}
                                highlightSymbol={hoverSymbol}
                            />
                        </div>
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Top-Coin Factor Radar
                            </h2>
                            <p className="muted text-xs mb-4">
                                Component z-scores for the top-ranked coins
                            </p>
                            <FactorRadar data={universe} topN={8} />
                        </div>
                    </div>

                    {/* Full sortable universe table. */}
                    <UniverseTable
                        data={processed}
                        sparklines={sparklines}
                        sparklineSymbols={sparklineSymbolSet}
                        sortKey={sortKey}
                        sortDesc={sortDesc}
                        onSort={handleSort}
                        onRowHover={setHoverSymbol}
                        onSymbolClick={setSelectedSymbol}
                    />
                </>
            )}

            {/* Tabbed detail pop-over (Price / Signal / Factors) for the
                clicked ticker. */}
            {selectedSymbol && (
                <CryptoDetailPopup
                    symbol={selectedSymbol}
                    onClose={() => setSelectedSymbol(null)}
                />
            )}
        </Layout>
    );
};

export default CryptoRankingPage;
